import styled from "styled-components";
import { toColorString } from "polished";
import { isValidRgb } from "./isValidRgb";
import { Rgb } from "./types";
import { BaseComponentProps } from "../types";
import "../../designTokens/variables.css";

const ensureHexStartsWithPoundSign = (hex: string) => {
  return hex.startsWith("#") ? hex : `#${hex}`;
};

type determineBackgroundColorArgs = Pick<SwatchProps, "hex" | "rgb">;

const determineBackgroundColor = ({
  hex,
  rgb,
}: determineBackgroundColorArgs) => {
  if (isValidRgb(rgb)) {
    return toColorString(rgb);
  }

  if (hex) {
    return ensureHexStartsWithPoundSign(hex);
  }

  return "#FFFFFF";
};

type StyledSwatchProps = {
  hex?: string;
  rgb?: Rgb;
};

const StyledSwatch = styled.div<StyledSwatchProps>`
  cursor: pointer;
  background-color: ${(props) => determineBackgroundColor(props)};
  border-radius: var(--size-border-radius-large);
  border: 1px solid var(--color-primary-200);
`;

type StyledImageSwatchProps = {
  imageUrl: string;
};

const StyledImageSwatch = styled(StyledSwatch)<StyledImageSwatchProps>`
  background-image: url("${(props) => props.imageUrl}");
  background-repeat: round;
`;

const StyledEmptySwatch = styled(StyledSwatch)`
  cursor: default;
  background-color: var(--color-neutral-000-full);
  overflow: hidden;
  position: relative;
  ::after {
    position: absolute;
    content: "";
    top: 50%;
    left: 50%;
    margin-left: -100%;
    height: 1px;
    width: 200%;
    background-color: var(--color-primary-200);
    transform-origin: center;
    transform: rotateZ(-45deg);
  }
`;

type SwatchContainerProps = {
  sideLengthInPx: number;
  selected?: boolean;
};

const SwatchContainer = styled.div<SwatchContainerProps>`
  height: ${(props) => props.sideLengthInPx + 2}px;
  width: ${(props) => props.sideLengthInPx + 2}px;
  padding: 1px;

  position: relative;
  ::before {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    content: "";
    z-index: 0;
    border: 1px solid var(--color-primary-400);
    border-radius: var(--size-border-radius-large);
    opacity: ${(props) => (props.selected ? 1 : 0)};
    transition: opacity ease-in-out 150ms;
  }
  > ${StyledSwatch}, > ${StyledImageSwatch} {
    position: relative;
    height: ${(props) => props.sideLengthInPx}px;
    width: ${(props) => props.sideLengthInPx}px;
  }
`;

type SwatchComponentProps = Omit<
  BaseSwatchProps,
  "sideLengthInPx" | "selected"
>;

const SwatchComponent = ({
  hex,
  imageUrl,
  rgb,
  ...props
}: SwatchComponentProps) => {
  if (imageUrl) {
    return <StyledImageSwatch imageUrl={imageUrl} {...props} />;
  }
  if (rgb || hex) {
    return <StyledSwatch hex={hex} rgb={rgb} {...props} />;
  }
  return <StyledEmptySwatch {...props} data-testid="EmptySwatch" />;
};

type BaseSwatchProps = SwatchProps &
  Pick<SwatchContainerProps, "sideLengthInPx">;

const BaseSwatch = ({
  hex,
  imageUrl,
  rgb,
  sideLengthInPx,
  selected,
  ...props
}: BaseSwatchProps) => {
  return (
    <SwatchContainer sideLengthInPx={sideLengthInPx} selected={selected}>
      <SwatchComponent
        data-testid="Swatch"
        hex={hex}
        imageUrl={imageUrl}
        rgb={rgb}
        {...props}
      />
    </SwatchContainer>
  );
};

type SwatchProps = BaseComponentProps<
  "div",
  {
    hex?: string;
    imageUrl?: string;
    rgb?: Rgb;
    selected?: boolean;
  }
>;

export const Swatch = ({ selected = false, ...props }: SwatchProps) => {
  return <BaseSwatch sideLengthInPx={18} selected={selected} {...props} />;
};

export const LargeSwatch = ({ selected = false, ...props }: SwatchProps) => {
  return <BaseSwatch sideLengthInPx={32} selected={selected} {...props} />;
};

export const EmptySwatch = ({ selected = false, ...props }: SwatchProps) => {
  return <BaseSwatch sideLengthInPx={18} selected={selected} {...props} />;
};

export const EmptyLargeSwatch = ({
  selected = false,
  ...props
}: SwatchProps) => {
  return <BaseSwatch sideLengthInPx={32} selected={selected} {...props} />;
};
